"use client";

/**
 * `CheckboxField` — boolean toggle rendered as a Radix Checkbox primitive.
 *
 * Why `Controller` instead of plain `register`: Radix Checkbox is a
 * controlled component whose state lives in `checked`/`onCheckedChange`,
 * not on a native `<input type="checkbox">` event surface. rhf's
 * `register` is built around DOM events, so wiring a Radix-style
 * primitive needs the `Controller` adapter that bridges the two.
 *
 * Layout: unlike text inputs, checkboxes look natural with the label
 * sitting to the right of the control. The shared `FieldShell` puts
 * label-on-top with control-below, which would be ugly for a single
 * checkbox. We hand-roll the layout here: a small flex row with the
 * checkbox first and the label second. We still emit the optional
 * description and inline error spans below the row, mirroring
 * FieldShell's other contracts.
 *
 * Accessibility: the Radix Checkbox accepts an `id` prop forwarded to a
 * hidden input; the visible button gets a matching `aria-labelledby` via
 * its own `<Label htmlFor>`. clicking the label toggles the checkbox as
 * users expect.
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { Checkbox, Label } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { CheckboxFieldDef } from "../types";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface CheckboxFieldProps {
  field: CheckboxFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function CheckboxField({
  field,
  pathPrefix,
}: CheckboxFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Controller
          name={path}
          control={form.control}
          render={({ field: rhf }) => (
            <Checkbox
              id={path}
              checked={Boolean(rhf.value)}
              onCheckedChange={(next) => {
                // Radix passes either `boolean` or the literal
                // `"indeterminate"`. The boolean field type can only
                // store `true | false`, so we coerce indeterminate to
                // false. A future indeterminate-aware checkbox would
                // need its own field def.
                rhf.onChange(next === true);
              }}
              onBlur={rhf.onBlur}
              ref={rhf.ref}
              aria-labelledby={`${path}-label`}
              aria-invalid={errorMessage !== undefined ? true : undefined}
              {...(field.disabled ? { disabled: true } : {})}
            />
          )}
        />
        <Label htmlFor={path} id={`${path}-label`}>
          {field.label}
        </Label>
      </div>
      {field.description ? (
        <p className="text-xs text-fg-muted">{field.description}</p>
      ) : null}
      {errorMessage ? (
        <p className="text-xs text-danger">{errorMessage}</p>
      ) : null}
    </div>
  );
}
